import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { lerPesoBalanca } from '../utils/balanca';
import { configEtiqueta, montarEtiquetaBalanca } from '../utils/etiquetaBalanca';
import {
  htmlEtiqueta, imprimirEtiqueta, cfgImpressaoSalva, salvarCfgImpressao, listarImpressorasEtiqueta,
} from '../utils/etiquetaPesagem';
import s from './PedidosOrcamentos.module.css';
import g from '../components/pdv/ModalMesasComandas.module.css';

// Pesagem e Etiquetas: pesa o produto na balança ligada a ESTE computador
// (agente de balança, USB/COM — qualquer modelo do catálogo do agente) e
// imprime a etiqueta EAN-13 no layout de Parâmetros → PDV → Etiqueta de
// balança, numa impressora de etiqueta comum. No PDV a etiqueta é
// reconhecida igual à impressa pela própria balança (produto + valor).
const fmtKg = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtR$ = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "0,265" e "0.265" valem o mesmo (peso não tem milhar)
const parseNum = v => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const INTERVALO_LEITURA_MS = 800;

const seloGrupo = {
  display: 'inline-block', minWidth: 18, padding: '0 5px', marginRight: 6, borderRadius: 9,
  background: '#1f2937', color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center',
};
const seloItem = {
  position: 'absolute', top: 4, left: 4, zIndex: 1, minWidth: 22, padding: '0 6px', borderRadius: 11,
  background: '#1565FF', color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: '22px',
  textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
};

// Mesma chave do PDV (liga/desliga a balança por computador): desligada lá,
// aqui o peso já começa digitado.
function balancaLigadaNoPdv() {
  try { return localStorage.getItem('pdv_balanca_ativa') !== 'false'; } catch (_) { return true; }
}

export default function PesagemEtiquetas() {
  const { usuario } = useAuth();
  const [cfgEtq, setCfgEtq] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [grupoSelId, setGrupoSelId] = useState(null);
  const [atalho, setAtalho] = useState('');
  const [busca, setBusca] = useState('');
  const [produto, setProduto] = useState(null);

  const [pesoBruto, setPesoBruto] = useState(0);
  const [balancaLigada] = useState(balancaLigadaNoPdv);
  const [manual, setManual] = useState(() => !balancaLigadaNoPdv());
  const [pesoDigitado, setPesoDigitado] = useState('');
  const [statusBalanca, setStatusBalanca] = useState({ ok: null, msg: 'Aguardando produto' });
  const [tara, setTara] = useState('');
  const [validadeDias, setValidadeDias] = useState('');

  const [imp, setImp] = useState(cfgImpressaoSalva);
  const [impressoras, setImpressoras] = useState(null);
  const [mostrarCfg, setMostrarCfg] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [avisoBalanca, setAvisoBalanca] = useState('');
  const [historico, setHistorico] = useState([]);
  const [imprimindo, setImprimindo] = useState(false);
  const buscaRef = useRef(null);
  const pesoRef = useRef(null);

  useEffect(() => {
    api.get(`/organizacoes/${usuario.organizacao_id}`)
      .then(({ data }) => setCfgEtq(configEtiqueta(data.pdv_config)))
      .catch(() => setCfgEtq(configEtiqueta(null)));
    api.get('/produtos', { params: { produto_balanca: 'true', todos: 'true' } })
      .then(({ data }) => setProdutos(Array.isArray(data) ? data : (data.produtos || [])))
      .catch(() => setErro('Não foi possível carregar os produtos de balança.'));
    api.get('/grupos').then(({ data }) => setGrupos(data || [])).catch(() => setGrupos([]));
    setTimeout(() => buscaRef.current?.focus(), 80);
  }, [usuario.organizacao_id]);

  useEffect(() => { salvarCfgImpressao(imp); }, [imp]);

  // Leitura contínua do peso enquanto há produto escolhido
  useEffect(() => {
    if (!produto || manual) return undefined;
    let ativo = true;
    let timer;
    const ler = async () => {
      try {
        const peso = await lerPesoBalanca();
        if (!ativo) return;
        setPesoBruto(Number(peso) || 0);
        setStatusBalanca({ ok: true, msg: 'Balança conectada' });
      } catch (e) {
        if (!ativo) return;
        setStatusBalanca({ ok: false, msg: e.message });
        // Sem balança: não fica esperando — já pede o peso digitado.
        setManual(true);
        setAvisoBalanca('Balança não encontrada — digite o peso.');
        setTimeout(() => pesoRef.current?.focus(), 50);
        return;
      }
      if (ativo) timer = setTimeout(ler, INTERVALO_LEITURA_MS);
    };
    ler();
    return () => { ativo = false; clearTimeout(timer); };
  }, [produto, manual]);

  const gruposComProduto = useMemo(() => {
    const ids = new Set(produtos.map(p => p.grupo_id).filter(Boolean));
    return grupos.filter(gr => ids.has(gr.id));
  }, [produtos, grupos]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toUpperCase();
    return produtos.filter(p => {
      if (grupoSelId && p.grupo_id !== grupoSelId) return false;
      if (!t) return true;
      return String(p.codigo_interno) === t || String(p.codigo_barras || '') === t ||
        (p.nome || '').toUpperCase().includes(t);
    }).slice(0, 300);
  }, [busca, produtos, grupoSelId]);

  useEffect(() => { setAtalho(''); }, [grupoSelId]);

  const escolher = p => {
    setProduto(p); setErro(''); setAviso(''); setAvisoBalanca('');
    setPesoBruto(0); setPesoDigitado('');
    setStatusBalanca({ ok: null, msg: 'Lendo a balança…' });
    if (manual) setTimeout(() => pesoRef.current?.focus(), 50);
    else document.activeElement?.blur?.();
  };

  const pesoLiquido = Math.max(0, Math.round(((manual ? parseNum(pesoDigitado) : pesoBruto) - parseNum(tara)) * 1000) / 1000);
  const precoKg = Number(produto?.preco_venda || 0);
  const total = Math.round(pesoLiquido * precoKg * 100) / 100;

  // Monta a etiqueta (ou o motivo de não dar pra montar)
  const etiqueta = useMemo(() => {
    if (!produto || !cfgEtq) return { erro: null };
    if (!(precoKg > 0)) return { erro: 'Produto sem preço de venda.' };
    if (!(pesoLiquido > 0)) return { erro: manual ? 'Digite o peso.' : 'Coloque o produto na balança.' };
    const codigoProduto = cfgEtq.usar === 'barras' ? produto.codigo_barras : produto.codigo_interno;
    try {
      const codigo = montarEtiquetaBalanca({ codigoProduto, valor: total, peso: pesoLiquido }, cfgEtq);
      const hoje = new Date();
      const dias = parseInt(validadeDias, 10);
      const validade = dias > 0 ? new Date(hoje.getTime() + dias * 86400000) : null;
      const dados = { nome: produto.nome, peso: pesoLiquido, tara: parseNum(tara), precoKg, total, codigo, data: hoje, validade };
      return { dados, html: htmlEtiqueta(dados, imp) };
    } catch (e) {
      return { erro: e.message };
    }
  }, [produto, cfgEtq, precoKg, pesoLiquido, total, tara, validadeDias, imp, manual]);

  const imprimir = useCallback(async () => {
    if (!etiqueta.html || imprimindo) return;
    setImprimindo(true); setErro(''); setAviso('');
    try {
      const como = await imprimirEtiqueta(etiqueta.html, imp);
      setHistorico(h => [{ ...etiqueta.dados, copias: imp.copias, hora: new Date() }, ...h].slice(0, 15));
      setAviso(como === 'direta' ? `Etiqueta enviada para ${imp.impressora}.` : 'Escolha a impressora de etiqueta na janela de impressão.');
    } catch (e) {
      setErro(e.message);
    } finally {
      setImprimindo(false);
    }
  }, [etiqueta, imp, imprimindo]);

  // Teclado (mesmo esquema do F4 Pesados no PDV):
  //  - dígito (fora de um campo, ou na busca vazia) = atalho: em "Todos"
  //    abre o grupo; no grupo, escolhe o produto pela posição na grade.
  //    Entra na hora se nenhum número maior começa com ele, senão espera
  //    outro dígito ou Enter;
  //  - Enter/F2 imprime; Esc: limpa atalho → solta o produto → volta pra Todos.
  const numerarItens = !!grupoSelId;
  const totalAtalhos = numerarItens ? filtrados.length : gruposComProduto.length;
  useEffect(() => {
    const escolherAtalho = n => {
      setAtalho('');
      if (numerarItens) { const p = filtrados[n - 1]; if (p) escolher(p); }
      else { const gr = gruposComProduto[n - 1]; if (gr) setGrupoSelId(gr.id); }
    };
    const onKey = e => {
      const ativo = document.activeElement;
      const emCampo = ativo && ['INPUT', 'SELECT', 'TEXTAREA'].includes(ativo.tagName);
      const naBuscaVazia = ativo === buscaRef.current && !busca;
      if (/^[0-9]$/.test(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey && (!emCampo || naBuscaVazia)) {
        e.preventDefault();
        const n = Number(atalho + e.key);
        if (n < 1 || n > totalAtalhos) { setAtalho(''); return; }
        if (n * 10 > totalAtalhos) escolherAtalho(n); else setAtalho(atalho + e.key);
        return;
      }
      if (atalho && e.key === 'Enter') { e.preventDefault(); escolherAtalho(Number(atalho)); return; }
      if (atalho && e.key === 'Backspace') { e.preventDefault(); setAtalho(a => a.slice(0, -1)); return; }
      if (e.key === 'F2' || (e.key === 'Enter' && produto && ativo !== buscaRef.current)) {
        e.preventDefault(); imprimir();
      } else if (e.key === 'Escape') {
        if (atalho) { e.preventDefault(); setAtalho(''); }
        else if (produto) { e.preventDefault(); setProduto(null); setBusca(''); ativo?.blur?.(); }
        else if (grupoSelId) { e.preventDefault(); setGrupoSelId(null); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const buscarImpressoras = async () => {
    setErro('');
    try { setImpressoras(await listarImpressorasEtiqueta()); }
    catch (_) { setErro('QZ Tray não encontrado neste computador — a etiqueta será impressa pela janela de impressão do navegador.'); setImpressoras([]); }
  };

  const setI = (k, v) => setImp(p => ({ ...p, [k]: v }));
  const cardStyle = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--content-bg)' }}>
      <div className={s.titleBar} style={{ display: 'flex', alignItems: 'center' }}>
        <span>⚖️ Pesagem e Etiquetas</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: !manual && statusBalanca.ok === false ? '#b91c1c' : !manual && statusBalanca.ok ? '#15803d' : 'var(--text-muted)' }}>
            {!balancaLigada ? 'Balança desligada neste computador (PDV) — digite o peso'
              : manual ? 'Peso digitado' : statusBalanca.msg}
          </span>
          <button className={s.btnErp} onClick={() => setMostrarCfg(v => !v)}>🖨️ Impressora</button>
          <button className={s.btnErp} onClick={() => window.close()} title="Fechar esta guia">✕ Fechar</button>
        </div>
      </div>

      {mostrarCfg && (
        <div style={{ ...cardStyle, margin: '12px 16px 0', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13 }}>Impressora de etiqueta:&nbsp;
            <select className={s.selectErp} style={{ width: 260 }} value={imp.impressora}
              onChange={e => setI('impressora', e.target.value)} onFocus={() => impressoras === null && buscarImpressoras()}>
              <option value="">Janela de impressão do navegador</option>
              {imp.impressora && !(impressoras || []).includes(imp.impressora) && <option value={imp.impressora}>{imp.impressora}</option>}
              {(impressoras || []).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className={s.btnErp} onClick={buscarImpressoras}>Buscar impressoras</button>
          <label style={{ fontSize: 13 }}>Etiqueta (mm):&nbsp;
            <input className={s.inputErp} style={{ width: 60 }} type="number" min="30" max="110" value={imp.largura}
              onChange={e => setI('largura', e.target.value)} /> x&nbsp;
            <input className={s.inputErp} style={{ width: 60 }} type="number" min="20" max="110" value={imp.altura}
              onChange={e => setI('altura', e.target.value)} />
          </label>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexBasis: '100%' }}>
            Configuração deste computador. Com o QZ Tray instalado, a etiqueta vai direto para a impressora escolhida;
            sem ele, abre a janela de impressão (escolha a impressora de etiqueta e o tamanho do papel lá).
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 1.5fr) minmax(360px, 1fr)', gap: 16, padding: 16, flex: 1, minHeight: 0 }}>
        {/* Produtos de balança */}
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px 0' }}>
            <input ref={buscaRef} className={s.inputErp} placeholder="Buscar por descrição ou código"
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && busca && filtrados[0]) { e.preventDefault(); escolher(filtrados[0]); e.target.blur(); } }} />
          </div>
          <div className={g.categorias} style={{ background: 'transparent' }}>
            <button type="button" className={`${g.categoriaBtn} ${!grupoSelId ? g.categoriaBtnAtiva : ''}`}
              onClick={() => setGrupoSelId(null)}>Todos</button>
            {gruposComProduto.map((gr, i) => (
              <button key={gr.id} type="button" className={`${g.categoriaBtn} ${grupoSelId === gr.id ? g.categoriaBtnAtiva : ''}`}
                onClick={() => setGrupoSelId(gr.id)} title={`Atalho: ${i + 1}`}>
                {!numerarItens && <span style={seloGrupo}>{i + 1}</span>}{gr.nome}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, padding: '0 14px 6px', color: atalho ? '#1565FF' : 'var(--text-muted)', fontWeight: atalho ? 700 : 400 }}>
            {atalho ? `Atalho: ${atalho} — digite o próximo número ou Enter`
              : numerarItens ? 'Digite o número do produto · Esc volta para Todos'
              : `${produtos.length} produto(s) de balança · digite o número do grupo`}
          </div>
          <div className={g.produtosWrap} style={{ paddingTop: 4 }}>
            {!filtrados.length ? (
              <div className={g.produtosVazio}>
                {produtos.length ? 'Nenhum produto encontrado.' : 'Nenhum produto marcado como "Produto de balança" no cadastro.'}
              </div>
            ) : (
              <div className={g.produtosGrade} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 8 }}>
                {filtrados.map((p, i) => (
                  <div key={p.id} className={g.produtoCard} onClick={() => escolher(p)}
                    style={{
                      padding: 6, gap: 3, position: 'relative',
                      ...(produto?.id === p.id ? { borderColor: '#1565FF', boxShadow: '0 0 0 2px #1565FF' } : {}),
                    }}>
                    {numerarItens && <span style={seloItem}>{i + 1}</span>}
                    {p.foto_url ? <img className={g.produtoFoto} src={p.foto_url} alt={p.nome} />
                      : <span className={g.produtoFotoVazia}>📦</span>}
                    <span className={g.produtoNome}>{p.nome}</span>
                    <span className={g.produtoPreco}>R$ {fmtR$(p.preco_venda)}/{p.unidade || 'kg'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pesagem */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, overflowY: 'auto' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: 15, fontWeight: 700, minHeight: 20 }}>{produto ? produto.nome : 'Escolha um produto'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10, textAlign: 'center' }}>
              <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>PESO LÍQ. (kg)</div>
                <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'monospace' }}>{fmtKg(pesoLiquido)}</div></div>
              <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>R$/kg</div>
                <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'monospace' }}>{fmtR$(precoKg)}</div></div>
              <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL R$</div>
                <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'monospace', color: 'var(--primary)' }}>{fmtR$(total)}</div></div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, alignItems: 'center', fontSize: 13 }}>
              {balancaLigada && (
                <label><input type="checkbox" checked={manual} onChange={e => setManual(e.target.checked)} /> Digitar peso</label>
              )}
              {manual && (
                <label>Peso (kg):&nbsp;
                  <input ref={pesoRef} className={s.inputErp} style={{ width: 90 }} placeholder="0,000" value={pesoDigitado}
                    onChange={e => setPesoDigitado(e.target.value)} />
                </label>
              )}
              <label>Tara (kg):&nbsp;
                <input className={s.inputErp} style={{ width: 80 }} placeholder="0,000" value={tara} onChange={e => setTara(e.target.value)} />
              </label>
              <label>Validade (dias):&nbsp;
                <input className={s.inputErp} style={{ width: 60 }} value={validadeDias} onChange={e => setValidadeDias(e.target.value.replace(/\D/g, ''))} />
              </label>
              <label>Cópias:&nbsp;
                <input className={s.inputErp} style={{ width: 55 }} type="number" min="1" max="50" value={imp.copias}
                  onChange={e => setI('copias', e.target.value)} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
              <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ minWidth: 200, height: 40, fontSize: 15 }}
                disabled={!etiqueta.html || imprimindo} onClick={imprimir}>
                {imprimindo ? 'Imprimindo…' : '🖨️ Imprimir etiqueta (Enter)'}
              </button>
              <span style={{ fontSize: 12, color: etiqueta.erro ? '#b45309' : 'var(--text-muted)' }}>
                {etiqueta.erro || (etiqueta.dados ? `Código ${etiqueta.dados.codigo}` : 'Esc troca de produto')}
              </span>
            </div>
            {avisoBalanca && (
              <div style={{ marginTop: 10, color: '#b45309', fontSize: 13, fontWeight: 600 }} title={statusBalanca.msg}>⚠️ {avisoBalanca}</div>
            )}
            {erro && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{erro}</div>}
            {aviso && <div style={{ marginTop: 10, color: '#15803d', fontSize: 13 }}>{aviso}</div>}
          </div>

          {etiqueta.html && (
            <div style={{ ...cardStyle, display: 'flex', justifyContent: 'center' }}>
              <iframe title="Prévia da etiqueta" srcDoc={etiqueta.html}
                style={{ width: `${Number(imp.largura) + 2}mm`, height: `${Number(imp.altura) + 2}mm`, border: '1px dashed var(--border)', background: '#fff' }} />
            </div>
          )}

          {historico.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Impressas agora</div>
              {historico.map((h, i) => (
                <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0' }}>
                  <span>{h.hora.toLocaleTimeString('pt-BR')} — {h.nome}</span>
                  <span>{fmtKg(h.peso)} kg · R$ {fmtR$(h.total)}{h.copias > 1 ? ` · ${h.copias}x` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
